import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import type { ApiResponse, PaginatedResponse } from '@/types/api'
import router from '@/router'
import { classifyAxiosAuthInterceptorAction } from '@/services/authErrorPolicy'

/** Laravel JSON lives under /api; local .env often omits the suffix (see docker-compose spa vs rukh-spa/.env). */
export function resolveApiBaseUrl(): string {
  const fallback = 'http://localhost:8000/api'
  const raw = (import.meta.env.VITE_API_URL || fallback).trim().replace(/\/+$/, '')
  if (raw.startsWith('/')) {
    return raw
  }

  return raw.endsWith('/api') ? raw : `${raw}/api`
}

class ApiService {
  private api: AxiosInstance
  /** Called synchronously after clearing storage when a 401 proves invalid PAT (see authErrorPolicy). */
  private _onHardLogout: (() => void) | null = null

  setHardLogoutCallback(cb: () => void): void {
    this._onHardLogout = cb
  }

  /** @deprecated Use setHardLogoutCallback */
  setOn401Callback(cb: () => void): void {
    this.setHardLogoutCallback(cb)
  }

  private performHardLogoutFromInterceptor(): void {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
    localStorage.removeItem('two_factor_token')
    sessionStorage.removeItem('timezone_modal_later_until')
    document.cookie = 'trusted_device_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict'
    this._onHardLogout?.()

    const publicRoutes = ['/', '/login', '/register', '/privacy-policy', '/terms-and-conditions']
    const currentPathForRedirect = router.currentRoute.value.path
    if (!publicRoutes.includes(currentPathForRedirect)) {
      sessionStorage.setItem('intended_url', currentPathForRedirect)
      router.replace('/')
    }
  }

  constructor() {
    const baseURL = resolveApiBaseUrl()
    
    this.api = axios.create({
      baseURL,
      // Default 4h to avoid ECONNABORTED on long operations; individual calls can still override
      timeout: 14400000, // 4 hours
      withCredentials: true, // Send cookies (e.g. trusted_device_token) on cross-origin requests
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      // 401/419 must reject for auth interceptor; other 4xx still resolve unless callers use
      // rejectIfClientError (post/put/patch/delete) or getWithResponse with manual status checks.
      validateStatus: (status) => status !== 401 && status !== 419 && status < 500
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor - add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        // If data is FormData, remove Content-Type header to let axios set it with boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type']
        }
        // Mark account GET requests as potentially silent (for soft-deleted accounts)
        if (config.url?.includes('/crm/accounts/') && config.method === 'get') {
          // Set validateStatus to accept 404s silently
          if (!config.validateStatus) {
            config.validateStatus = (status) => status === 200 || status === 404
          }
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor - handle errors globally
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        const s = response.status
        const data = response.data as Record<string, unknown> | undefined
        if (s === 403 && data?.requires_2fa_setup === true) {
          const path = router.currentRoute.value.path
          if (path !== '/settings/two-factor') {
            const key = 'rukh_2fa_setup_redirect_at'
            const prev = sessionStorage.getItem(key)
            const now = Date.now()
            if (!prev || now - Number(prev) > 30_000) {
              sessionStorage.setItem(key, String(now))
              router.replace('/settings/two-factor').catch(() => {})
            }
          }
          return Promise.reject(
            new AxiosError(
              'Two-factor authentication setup is required.',
              'ERR_BAD_REQUEST',
              response.config,
              response.request,
              response
            )
          )
        }
        return response
      },
      async (error) => {
        const status = error.response?.status
        const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout')
        
        // CRITICAL: Don't treat timeout errors as authentication failures
        // Timeouts can occur during long operations (like CSV imports) and don't mean the user is logged out
        if (isTimeout) {
          // For timeout errors, just reject - don't clear auth or redirect
          return Promise.reject(error)
        }
        
        // Log error to backend (for 4xx/5xx errors)
        // Skip logging 404s for account endpoints (expected when account is soft-deleted)
        const url = error.config?.url || ''
        const isAccount404 = status === 404 && url.includes('/crm/accounts/') && error.config?.method === 'get'
        
        if (status && (status >= 400) && !isAccount404) {
          try {
            const errorLogger = (await import('./errorLogger')).default
            const requestId = error.response?.headers?.['x-request-id'] || null
            
            // Determine domain and action from request
            const method = error.config?.method || 'GET'
            let domain = 'ui'
            let action = 'api_request'
            
            if (url.includes('inquiries')) {
              domain = 'inquiries'
              if (url.includes('local-inquiries')) {
                action = 'local_inquiry_request'
              } else if (url.includes('walmart-inquiries')) {
                action = 'walmart_inquiry_request'
              } else {
                action = 'inquiry_request'
              }
            } else if (url.includes('surveys')) {
              domain = 'install'
              action = 'survey_request'
            } else if (url.includes('auth')) {
              domain = 'auth'
              action = 'auth_request'
            } else if (url.includes('/amazon/')) {
              domain = 'sales'
              if (url.includes('initiatives')) {
                action = 'initiative_request'
              } else if (url.includes('launches')) {
                action = 'launch_request'
              } else {
                action = 'amazon_request'
              }
            }
            
            // Refine action based on method and path
            if (method === 'POST') action = action.replace('_request', '_create')
            else if (method === 'PUT' || method === 'PATCH') action = action.replace('_request', '_update')
            else if (method === 'DELETE') action = action.replace('_request', '_delete')
            
            errorLogger.logError(
              domain,
              action,
              router.currentRoute.value.path,
              error,
              undefined,
              {
                url: error.config?.url,
                method: method.toUpperCase(),
                status: status,
                response_data: error.response?.data ? JSON.stringify(error.response.data) : undefined,
              },
              requestId
            )
          } catch (loggingError) {
            // Silently fail if error logging fails
            console.error('Failed to log error', loggingError)
          }
        }
        
        const authAction = classifyAxiosAuthInterceptorAction(error)
        if (authAction === 'hard_logout') {
          this.performHardLogoutFromInterceptor()
        } else if (status === 419) {
          console.warn('[auth] 419 received; Bearer session not cleared. If this persists, reload the page.', {
            url: error.config?.url,
          })
        }
        
        // Don't redirect on 403 errors - let components handle permission errors
        // Components can show error messages or hide UI elements based on permissions
        // The route guard already handles route-level permission checks
        // Only redirect on 401 (authentication failures), not 403 (authorization failures)
        
        // Silently handle 404s for account endpoints (soft-deleted accounts)
        // Return a resolved promise with a fake response to prevent console errors
        if (isAccount404) {
          return Promise.resolve({
            status: 404,
            statusText: 'Not Found',
            data: null,
            headers: {},
            config: error.config
          } as AxiosResponse)
        }
        
        // For other errors (404, 500, etc.) - let the component handle it
        return Promise.reject(error)
      }
    )
  }

  /** Mutation helpers must throw on 4xx — global validateStatus treats 422/403 as resolved. */
  private rejectIfClientError(response: AxiosResponse): void {
    if (response.status >= 400) {
      const data = response.data as Record<string, unknown> | undefined
      let message =
        typeof data?.message === 'string' && data.message
          ? data.message
          : `Request failed with status ${response.status}`

      const errors = data?.errors
      if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        const first = Object.values(errors as Record<string, unknown>)[0]
        const fieldMessage = Array.isArray(first) ? first[0] : first
        if (typeof fieldMessage === 'string' && fieldMessage.trim() !== '') {
          message = fieldMessage
        }
      }

      throw new AxiosError(
        message,
        AxiosError.ERR_BAD_REQUEST,
        response.config,
        response.request,
        response
      )
    }
  }

  // Generic HTTP methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(url, config)
    return response.data
  }

  // Get method that returns full response (useful for checking status codes)
  async getWithResponse<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    // For account requests, ensure validateStatus accepts 404s
    const isAccountRequest = url.includes('/crm/accounts/')
    
    // For account 404s, temporarily suppress console errors
    let originalConsoleError: typeof console.error | null = null
    let originalConsoleWarn: typeof console.warn | null = null
    
    if (isAccountRequest) {
      originalConsoleError = console.error
      originalConsoleWarn = console.warn
      
      // Override console.error to filter out account 404 errors
      console.error = (...args: any[]) => {
        const errorStr = args.map(a => String(a)).join(' ')
        const isAccount404Error = errorStr.includes('/crm/accounts/') && 
                                 (errorStr.includes('404') || errorStr.includes('Not Found'))
        if (!isAccount404Error) {
          originalConsoleError!.apply(console, args)
        }
      }
      
      // Override console.warn to filter out account 404 warnings
      console.warn = (...args: any[]) => {
        const warnStr = args.map(a => String(a)).join(' ')
        const isAccount404Warn = warnStr.includes('/crm/accounts/') && 
                                (warnStr.includes('404') || warnStr.includes('Not Found'))
        if (!isAccount404Warn) {
          originalConsoleWarn!.apply(console, args)
        }
      }
    }
    
    const finalConfig: AxiosRequestConfig = {
      ...config,
      validateStatus: isAccountRequest 
        ? (status: number) => status === 200 || status === 404
        : config?.validateStatus
    }
    
    try {
      const response = await this.api.get<T>(url, finalConfig)
      
      // Restore console methods
      if (isAccountRequest && originalConsoleError) {
        console.error = originalConsoleError
        console.warn = originalConsoleWarn!
      }
      
      return response
    } catch (error: any) {
      // Restore console methods even on error
      if (isAccountRequest && originalConsoleError) {
        console.error = originalConsoleError
        console.warn = originalConsoleWarn!
      }
      
      throw error
    }
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post(url, data, config)
    this.rejectIfClientError(response)
    return response.data
  }

  // Post method that returns the full response (headers, status) — e.g. a blob download whose
  // filename and counts travel in headers. Client errors are rejected like post() unless the
  // caller opts out (rejectClientErrors: false) to read an accepted 4xx body itself.
  async postWithResponse<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    options: { rejectClientErrors?: boolean } = {}
  ): Promise<AxiosResponse<T>> {
    const response = await this.api.post<T>(url, data, config)
    if (options.rejectClientErrors !== false) {
      this.rejectIfClientError(response)
    }
    return response
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put(url, data, config)
    this.rejectIfClientError(response)
    return response.data
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch(url, data, config)
    this.rejectIfClientError(response)
    return response.data
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete(url, config)
    this.rejectIfClientError(response)
    return response.data
  }

  // Paginated requests
  async getPaginated<T = any>(url: string, params?: any): Promise<PaginatedResponse<T>> {
    const response = await this.api.get(url, { params })
    return response.data
  }

  // File upload
  async uploadFile(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      }
    })

    return response.data
  }

  // Upload file to DigitalOcean Spaces via /uploader endpoint
  async uploadToSpaces(
    folder: string,
    file: File,
    uuid?: string,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<{
    path: string
    url: string
    filename: string
    original_filename: string
    size: number
    mime_type: string
    folder: string
    uuid: string | null
  }>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)
    if (uuid) {
      formData.append('uuid', uuid)
    }

    const response = await this.api.post('/uploader', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      }
    })

    return response.data
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    return this.get('/health')
  }
}

export const apiService = new ApiService()
export default apiService
